import { Location } from '@angular/common';
import { AfterViewInit, Component, DestroyRef, HostListener, inject, OnInit, Renderer2 } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Meta } from '@angular/platform-browser';
import { NavigationEnd, Router } from '@angular/router';
import { CdcJsService } from '@spartacus/cdc/root';
import { WindowRef } from '@spartacus/core';
import { CurrentProductService, PageLayoutService } from '@spartacus/storefront';
import { fromEvent } from 'rxjs';
import { distinctUntilChanged, map, pairwise, take, throttleTime } from 'rxjs/operators';
import { CategoryService } from './features/services/category.service';
import { ClickService } from './features/services/click.service';
import { DownloadService } from './features/services/download.service';
import { GoogleSearchConsoleService } from './features/services/google-search-console.service';
import { ScrollPercentageService } from './features/services/scroll-percentage.service';

export enum Direction {
  none = 'None',
  up = 'Up',
  down = 'Down',
}

@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss'],
  standalone: false,
})
export class AppComponent implements AfterViewInit, OnInit {
  private renderer = inject(Renderer2);
  private winRef = inject(WindowRef);
  private scrollPercentageService = inject(ScrollPercentageService);
  private clickService = inject(ClickService);
  private downloadService = inject(DownloadService);
  private router = inject(Router);
  private pageLayoutService = inject(PageLayoutService);
  private meta = inject(Meta);
  // private commonService = inject(CommonCdcEnabelService);
  private cdcJsService = inject(CdcJsService);
  private categoryService = inject(CategoryService);
  protected currentProductService = inject(CurrentProductService);
  private location = inject(Location);
  private googleSearchConsole = inject(GoogleSearchConsoleService);
  private destroyRef = inject(DestroyRef);

  private minSrcollTopToHide = 100;

  // Cache file extension regex for better performance
  private readonly FILE_EXTENSION_REGEX =
    /.pdf|.xlsx?|.docx?|.txt|.rtf|.csv|.exe|.key|.pp(s|t|tx)|.7z|.pkg|.rar|.gz|.zip|.avi|.mov|.mp4|.mpe?g|.wmv|.midi?|.mp3|.wav|.wma/;

  ngOnInit(): void {
    this.router.events.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((event) => {
      if (event instanceof NavigationEnd) {
        this.scrollPercentageService.setNewPageProperties();
      }
    });

    this.pageLayoutService.page$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((page) => {
      this.updateMetaTags(page);
      this.handleUrlUpdates(page);
    });

    this.googleSearchConsole.addVerificationMetaTag();
  }

  private updateMetaTags(page: any): void {
    this.meta.updateTag({ name: 'page-name', content: page.name || 'Hisense' });

    const pageIdTag = { name: 'page-id', content: page.pageId || '' };
    if (this.meta.getTag('name=page-id')) {
      this.meta.updateTag(pageIdTag);
    } else {
      this.meta.addTag(pageIdTag);
    }
  }

  private handleUrlUpdates(page: any): void {
    switch (page.type) {
      case 'ProductPage':
        this.handleProductPageUrl();
        break;
      case 'CategoryPage':
        this.handleCategoryPageUrl();
        break;
      case 'ContentPage':
        this.handleContentPageUrl();
        break;
    }
  }

  private handleProductPageUrl(): void {
    this.currentProductService
      .getProduct()
      .pipe(take(1))
      .subscribe((data) => {
        const newUrl = decodeURIComponent(data?.url ?? '');
        const urlTree = this.router.parseUrl(this.router.url);

        const normalizedNewUrl = this.normalizeUrl(newUrl); // Normalized `newUrl`
        const normalizedCurrentUrl = this.normalizeUrl(urlTree.toString()); // Normalized router URL

        if (normalizedNewUrl !== normalizedCurrentUrl) this.location.replaceState(newUrl);
      });
  }

  private normalizeUrl(url: string) {
    // Using URL constructor for consistency and normalization
    const tempUrl = new URL(url, this.winRef.location.origin);
    return tempUrl.pathname.replace(/\/+$/, ''); // Remove trailing slash
  }

  private handleCategoryPageUrl(): void {
    const url = this.router?.url?.split('/').pop() || '';
    const [category, params] = url.includes('?') ? url.split('?') : [url, ''];

    if (category) {
      this.categoryService
        .getCategory(category)
        .pipe(take(1))
        .subscribe((data) => {
          const newUrl = decodeURIComponent(data?.url ?? '') + (params ? `?${params}` : '');
          if (!this.location.isCurrentPathEqualTo(newUrl)) {
            this.location.replaceState(newUrl);
          }
        });
    }
  }

  private handleContentPageUrl(): void {
    this.pageLayoutService.page$.pipe(take(1)).subscribe((page: any) => {
      if (page?.pageUrl) {
        const newUrl = decodeURIComponent(page.pageUrl);
        const urlTree = this.router.parseUrl(this.router.url);
        const queryParams =
          urlTree.queryParams && Object.keys(urlTree.queryParams).length > 0
            ? '?' + new URLSearchParams(urlTree.queryParams as any).toString()
            : '';

        const fullNewUrl = newUrl + queryParams;

        if (!this.location.isCurrentPathEqualTo(fullNewUrl)) {
          this.location.replaceState(fullNewUrl);
        }
      }
    });
  }

  ngAfterViewInit(): void {
    this.initCdc();

    if (this.winRef.isBrowser()) {
      const win = this.winRef.nativeWindow!;
      const header = win.document.getElementsByTagName('header')[0];

      // Observe mobile menu state changes and prevent body scrolling
      this.observeMobileMenuState(win, header);

      // Use passive scroll events for better performance since we don't preventDefault()
      fromEvent(win, 'scroll', { passive: true })
        .pipe(
          takeUntilDestroyed(this.destroyRef),
          throttleTime(250), // Increased from 200ms for better performance
          map(() => win.scrollY),
          pairwise(),
          map(([y1, y2]): Direction => {
            this.scrollPercentageService.dispatchGa4GgScrollEvent(win.document, y2);
            return y2 < y1 ? Direction.up : y2 > this.minSrcollTopToHide ? Direction.down : Direction.none;
          }),
          distinctUntilChanged()
        )
        .subscribe((dir) => {
          const body = win.document.body;
          const hasScrollDown = body.classList.contains('scroll-down');

          if (dir === Direction.down) {
            if (!hasScrollDown) {
              this.renderer.removeClass(body, 'scroll-up');
              this.renderer.addClass(body, 'scroll-down');
              this.renderer.addClass(header, 'hide');
            }
          } else {
            this.renderer.removeClass(body, 'scroll-down');
            this.renderer.addClass(body, 'scroll-up');
            this.renderer.removeClass(header, 'hide');
          }
        });

      // Use passive scrollend events for better performance
      fromEvent(win, 'scrollend', { passive: true })
        .pipe(
          takeUntilDestroyed(this.destroyRef),
          map(() => win.scrollY)
        )
        .subscribe((y) => {
          const body = win.document.body;
          if (!body.classList.contains('scroll-up') && y < this.minSrcollTopToHide) {
            this.renderer.removeClass(body, 'scroll-down');
            this.renderer.addClass(body, 'scroll-up');
            this.renderer.removeClass(header, 'hide');
          }
        });
    }
  }

  /**
   * Observe mobile menu state changes and manage body scroll prevention
   * Optimized: guard checks, aria-expanded support, rAF batching, cancel rAF on destroy
   */
  private observeMobileMenuState(win: Window, header: HTMLElement): void {
    const body = win?.document?.body;

    // Guard against missing DOM elements in SSR or unexpected document structure
    if (!header || !body) {
      return;
    }

    const NO_SCROLL_CLASS = 'no-scroll';
    const EXPANDED_CLASS = 'is-expanded';

    // Memoized state to avoid redundant DOM writes
    let isMenuExpanded = header.classList.contains(EXPANDED_CLASS) || header.getAttribute('aria-expanded') === 'true';
    let isBodyNoScroll = body.classList.contains(NO_SCROLL_CLASS);

    // Use rAF to batch DOM updates when mutations arrive quickly
    let rafId: number | null = null;

    const applyState = (expanded: boolean) => {
      // If nothing changed, skip
      if (expanded === isMenuExpanded) return;

      isMenuExpanded = expanded;

      // Cancel any pending rAF and schedule a new one
      if (rafId !== null) {
        cancelAnimationFrame(rafId);
        rafId = null;
      }

      rafId = requestAnimationFrame(() => {
        if (isMenuExpanded && !isBodyNoScroll) {
          this.renderer.addClass(body, NO_SCROLL_CLASS);
          isBodyNoScroll = true;
        } else if (!isMenuExpanded && isBodyNoScroll) {
          this.renderer.removeClass(body, NO_SCROLL_CLASS);
          isBodyNoScroll = false;
        }
        rafId = null;
      });
    };

    const observer = new MutationObserver((mutations) => {
      // Quick check if any relevant attribute changed
      let relevant = false;
      for (const m of mutations) {
        if (m.type === 'attributes' && (m.attributeName === 'class' || m.attributeName === 'aria-expanded')) {
          relevant = true;
          break;
        }
      }
      if (!relevant) return;

      // Determine new expanded state (support both class and aria-expanded toggles)
      const newExpanded = header.classList.contains(EXPANDED_CLASS) || header.getAttribute('aria-expanded') === 'true';
      applyState(newExpanded);
    });

    observer.observe(header, {
      attributes: true,
      attributeFilter: ['class', 'aria-expanded'],
      subtree: false,
    });

    // Clean up observer and any pending rAF on component destroy
    this.destroyRef.onDestroy(() => {
      observer.disconnect();
      if (rafId !== null) {
        cancelAnimationFrame(rafId);
        rafId = null;
      }
    });
  }

  // set focus on search input after search icon click
  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent): void {
    // Skip if SSR (no window available)
    if (!this.winRef.isBrowser()) {
      return;
    }

    const el = event.target as HTMLElement;

    // Ensure keyboard accessibility - early return for non-interactive elements
    if (!(el instanceof HTMLButtonElement || el instanceof HTMLAnchorElement)) {
      return;
    }

    // const isCxIcon = el.tagName === 'CX-ICON';
    // if (isCxIcon && el.classList.contains('fa-search')) {
    //   const input = this.winRef.nativeWindow!.document.querySelector('input');
    //   if (input) {
    //     requestAnimationFrame(() => {
    //       (input as HTMLElement).focus();
    //     });
    //   }
    // }

    // Handle button clicks
    if (el instanceof HTMLButtonElement) {
      this.handleButtonClick(el);
    }
    // Handle anchor clicks
    else if (el instanceof HTMLAnchorElement && el.href) {
      this.handleAnchorClick(el);
    }
  }

  private handleButtonClick(button: HTMLButtonElement): void {
    // Early return if no formAction
    if (!button.formAction) return;

    try {
      const url = new URL(button.formAction);
      if (this.urlWithFileExtension(url)) {
        this.downloadService.dispatchGa4GgDownloadEvent();
      }
    } catch (e) {
      console.error('Invalid URL in button formAction:', e);
    }
  }

  private handleAnchorClick(anchor: HTMLAnchorElement): void {
    // Early return if no href
    if (!anchor.href) return;

    try {
      const url = new URL(anchor.href);
      this.clickService.dispatchGa4GgAnchorClickEvent(anchor);
      if (this.urlWithFileExtension(url)) {
        this.downloadService.dispatchGa4GgDownloadEvent();
      }
    } catch (e) {
      console.error('Invalid URL in anchor href:', e);
    }
  }

  private urlWithFileExtension(url: URL): boolean {
    // Early return if no pathname
    if (!url.pathname || url.pathname.indexOf('/') === -1) {
      return false;
    }

    const splittedPathname: string[] = url.pathname.split('/');

    if (splittedPathname.length === 0) {
      return false;
    }

    // Use cached regex for better performance
    return this.FILE_EXTENSION_REGEX.test(splittedPathname[splittedPathname.length - 1]);
  }

  private initCdc(): void {
    if (this.winRef.isBrowser()) {
      // Browser: Use requestIdleCallback for better performance
      const nativeWindow = this.winRef.nativeWindow;
      if (nativeWindow && 'requestIdleCallback' in nativeWindow) {
        nativeWindow.requestIdleCallback(
          () => {
            this.cdcJsService.initialize();
            this.cdcJsService.loadCdcJavascript();
          },
          { timeout: 50 }
        );
      } else {
        // Fallback: load immediately after current event loop
        setTimeout(() => {
          this.cdcJsService.initialize();
          this.cdcJsService.loadCdcJavascript();
        }, 0);
      }
    } else {
      // SSR: Initialize immediately for proper hydration
      this.cdcJsService.initialize();
      this.cdcJsService.loadCdcJavascript();
    }
  }
}
